import { useState } from "react";
import { motion } from "framer-motion";
import { Eyebrow, Reveal } from "../components/Shared";
import { IMAGES, PRODUCTION_LINE } from "../data/site";

const ProductionStep = ({ step, index, active, onActivate }) => (
  <Reveal delay={index * 0.06}>
    <button
      type="button"
      onClick={() => onActivate(index)}
      data-testid={`capability-step-${step.num}`}
      className={`group relative flex w-full items-start gap-6 rounded-xl border p-6 text-left transition-[border-color,background-color,box-shadow] duration-300 sm:p-7 ${
        active
          ? "border-brand/30 bg-white shadow-[0_8px_24px_-16px_rgba(23,78,166,0.35)]"
          : "border-line bg-white/60 hover:border-brand/20 hover:bg-white"
      }`}
    >
      <span
        className={`text-xs font-semibold tracking-[0.16em] transition-colors duration-300 ${
          active ? "text-brand2" : "text-slate"
        }`}
      >
        {step.num}
      </span>
      <span className="flex-1">
        <span className="label-caps block text-[10px]">{step.type}</span>
        <span className="mt-1.5 block font-display text-lg font-bold tracking-tight text-navy sm:text-xl">
          {step.name}
        </span>
        {active && (
          <motion.span
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            transition={{ duration: 0.35 }}
            className="mt-3 block text-sm leading-relaxed text-slate"
          >
            {step.desc}
          </motion.span>
        )}
      </span>
      <span
        className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full border transition-colors duration-300 ${
          active ? "border-brand2 bg-brand2" : "border-line bg-white group-hover:border-brand2/50"
        }`}
      />
    </button>
  </Reveal>
);

const CapabilitiesSection = () => {
  const [active, setActive] = useState(0);

  return (
    <section className="relative overflow-hidden bg-tint py-24 sm:py-32" data-testid="capabilities-section">
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <Reveal>
              <Eyebrow>03 — Infrastructure &amp; Capabilities</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-2xl font-bold leading-tight tracking-tight text-navy sm:text-3xl lg:text-4xl">
                Built for Precision.
                <br />
                Equipped for Scale.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base leading-relaxed text-slate">
                Our strength lies in state-of-the-art infrastructure supported by a highly skilled technical team.
                Every print undergoes rigorous quality checks monitored by trained professionals.
              </p>
            </Reveal>
            <Reveal delay={0.3} className="mt-10">
              <div className="relative overflow-hidden rounded-2xl border border-line bg-white shadow-sm">
                <img
                  src={IMAGES.roller}
                  alt="Close-up of offset press rollers"
                  className="h-64 w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
                <p className="absolute bottom-4 left-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white">
                  Production Line — 8 Stages
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-3">
            <div className="relative">
              <motion.div
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute bottom-6 left-0 top-6 hidden w-px origin-top bg-gradient-to-b from-brand2 via-brand/20 to-brand2 sm:block"
              />
              <div className="space-y-3 sm:pl-8">
                {PRODUCTION_LINE.map((step, i) => (
                  <ProductionStep key={step.num} step={step} index={i} active={active === i} onActivate={setActive} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CapabilitiesSection;
